import { Client as SFrameClientDefault } from '@vonage/js-sframe';
import normalizeKeyDefault from './normalizeKey';
import createLogger from '../helpers/log';

const logger = createLogger('StandardSFrameClient');

const addDecryptEventsToClient = async (client) => {
  await client.setEnableOnSuccess(true);
  let pendingDecryptionRestored = false;

  // The SFrame client dispatches events error or success for every frame but we only want to surface
  // state changes e.g. authentication error -> success means we emit 'decryptionRestored'.
  // To achieve this we add a listener only for error at first, then when an authentication error event
  // is fired we surface the decryptFailed event and add the success event listener. When a success event
  // is fired we remove the success listener and surface the decryptionRestored event.
  // Other potential error or warning events are not surfaced but logged.
  const onError = (e) => {
    if (e?.subtype === 'FailedToAuthenticate' && !pendingDecryptionRestored) {
      pendingDecryptionRestored = true;
      client.addEventListener('success', onSuccess); // eslint-disable-line no-use-before-define
      client.dispatchEvent(new Event('decryptFailed'));
    } else if (e?.subtype !== 'FailedToAuthenticate') {
      logger.error(`SFrameClient error: type ${e.subtype} message ${e.message}`);
    }
  };
  const onSuccess = () => {
    client.removeEventListener('success', onSuccess);
    client.dispatchEvent(new Event('decryptionRestored'));
    pendingDecryptionRestored = false;
  };
  const onWarning = (e) => {
    logger.warn(`SFrameClient warning: type ${e.subtype} message ${e.message}`);
  };
  client.addEventListener('error', onError);
  client.addEventListener('warning', onWarning);
};

const create = async (senderId, deps = {}) => {
  const SFrameClient = deps.SFrameClient || SFrameClientDefault;
  const normalizeKey = deps.normalizeKey || normalizeKeyDefault;

  const client = await SFrameClient.create(senderId);

  // Cache original method, since we need to ensure it's compatible
  // with it's legacy SFrame counterpart
  client._setReceiverEncryptionKey = client.setReceiverEncryptionKey;
  client.setReceiverEncryptionKey = async (_senderId, key) => {
    const normalizedKey = await normalizeKey(key);

    return client._setReceiverEncryptionKey(_senderId, normalizedKey);
  };

  // Cache original method, since we need to ensure it's compatible
  // with it's legacy SFrame counterpart
  client._setSenderEncryptionKey = client.setSenderEncryptionKey;
  client.setSenderEncryptionKey = async (key) => {
    const normalizedKey = await normalizeKey(key);

    return client._setSenderEncryptionKey(normalizedKey);
  };

  addDecryptEventsToClient(client);

  return client;
};

const StandardSFrameClient = {
  create,
};

export default StandardSFrameClient;
